import type { QueryClient } from "@tanstack/react-query";
import { getItem, getPlayerPlaybackInfo } from "../api/jellyfin";
import type { Credentials } from "../storage/credentials";
import {
  PLAYER_PLAYBACK_INFO_STALE_MS,
  playerPlaybackInfoQueryKey,
} from "./playbackQueryKeys";
import {
  emptySourcePreference,
  rankPlaybackSources,
} from "./sourceRanking";
import { productProfile } from "../productProfile";

const PLAYBACK_ITEM_METADATA_STALE_MS = 5 * 60 * 1000;

export function prefetchPlaybackItemMetadata(
  queryClient: QueryClient,
  credentials: Credentials,
  itemId: string,
) {
  return queryClient.prefetchQuery({
    queryKey: ["player", "item", itemId],
    queryFn: () => getItem(credentials, itemId),
    staleTime: PLAYBACK_ITEM_METADATA_STALE_MS,
  });
}

export async function prefetchPlaybackInfoForItem(
  queryClient: QueryClient,
  credentials: Credentials,
  itemId: string,
  preferredAudioLanguage: string,
) {
  if (!productProfile.aggressiveOptimizationsEnabled) return;
  const item = await queryClient.fetchQuery({
    queryKey: ["player", "item", itemId],
    queryFn: () => getItem(credentials, itemId),
    staleTime: PLAYBACK_ITEM_METADATA_STALE_MS,
  });
  const source = rankPlaybackSources(
    item.MediaSources ?? [],
    emptySourcePreference,
    preferredAudioLanguage,
  ).find((entry) => !entry.ignored)?.source;
  const sourceId = source?.Id ?? "";

  return queryClient.fetchQuery({
    queryKey: playerPlaybackInfoQueryKey(itemId, sourceId),
    queryFn: () =>
      getPlayerPlaybackInfo(credentials, itemId, sourceId || undefined),
    staleTime: PLAYER_PLAYBACK_INFO_STALE_MS,
  });
}
